import { APP_ROUTES } from '@/common/constants'
import type { User, Account } from '@/common/types'

/**
 * Whether the active account needs the participant to confirm their details.
 * Pathname-independent: callers gating other redirects (e.g. onboarding) on this
 * must NOT use getAccountConfirmationRedirect for that, since its own pathname
 * loop-guard goes null while sitting on the confirmation page - using it to gate
 * another redirect would "unblock" that redirect the moment confirmation is showing.
 */
export function isAccountConfirmationRequired(account: Account | null): boolean {
    return account?.confirmationStatus === 'REQUIRED'
}

/**
 * Determines if a redirect to account confirmation is needed.
 * Pure function for easy testing and memoization.
 *
 * @returns Route to redirect to, or null if no redirect needed
 */
export function getAccountConfirmationRedirect(
    user: User | null,
    account: Account | null,
    isAccountSwitching: boolean,
    currentPathname: string,
): string | null {
    // Don't redirect during account switching or if no user
    if (isAccountSwitching || !user) return null

    // Don't redirect if already on confirmation page (prevent infinite loops)
    if (currentPathname === APP_ROUTES.ACCOUNT_CONFIRMATION) return null

    // Redirect to confirmation if the account is awaiting the participant's confirmation
    if (isAccountConfirmationRequired(account)) {
        return APP_ROUTES.ACCOUNT_CONFIRMATION
    }

    // All checks passed
    return null
}
